import { BillingCtaButton } from '~/design-system/components/v1_5/Settings';
import { cn } from '~/lib/utils';
import { ScrollRevealHeadline } from '~/landing_page/components/ScrollRevealHeadline';
import landingType from '~/landing_page/styles/landingTypography.module.css';
import styles from './CTASection.module.css';

const SIGN_IN_HREF = '/sign-in';

export interface CTASectionProps {
  className?: string;
  headline?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  ctaHref?: string;
  /** The page's tone, not the reader's appearance — same call every other v2 section makes. */
  tone?: 'light' | 'dark';
}

export function CTASection({
  className,
  headline = 'Your next story starts here.',
  ctaLabel = 'Build my story',
  onCtaClick,
  ctaHref,
  tone = 'light',
}: CTASectionProps) {
  // The button previously had neither an href nor a handler, so it rendered and did nothing on
  // click. Navigating here rather than defaulting ctaHref, because Button's `asChild` merges its
  // props onto the content wrapper instead of the anchor, which leaves only the label clickable
  // rather than the whole pill.
  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick();
      return;
    }
    window.location.href = ctaHref ?? SIGN_IN_HREF;
  };

  return (
    <section
      className={cn(landingType.sectionVerticalPadding, styles.section, className)}
      data-name="CTASection"
    >
      <div className={cn(landingType.sectionInset, styles.container)}>
        <div className={styles.content}>
          <ScrollRevealHeadline className={cn(landingType.sectionHeadline, styles.headline)}>
            {headline}
          </ScrollRevealHeadline>

          <div className={styles.actions}>
            {/*
              * The billing surface's `key` pill — the same button the pricing cards and the cookie
              * banner draw, which on the dark ground is white with a dark label. It replaces a legacy
              * `Button variant="upgrade"` restyled here with its own radius and padding, so the page's
              * last CTA was a different object from every other CTA on it.
              *
              * One branch, not two. The `ctaHref` branch used `asChild`, and the comment above
              * explains that merges Button's props onto the content wrapper rather than the anchor —
              * only the label was clickable. `ctaHref` folds into the click handler instead, so the
              * whole pill is the target either way. No caller passes it today.
              */}
            <BillingCtaButton skin="key" theme={tone} onClick={handleCtaClick}>
              {ctaLabel}
            </BillingCtaButton>
          </div>
        </div>
      </div>
    </section>
  );
}

CTASection.displayName = 'CTASection';
